<script setup lang="ts">
import { CircleLoader } from "lorga-ui";
import WhiteBox from "@/features/fahrer/components/WhiteBox.vue";
import useAuftragFahrer from "../api/useAuftragFahrer";
import FahrzeitEintragen from "@/features/fahrer/actions/FahrzeitEintragen.vue";
import { toRefs } from "vue";
import ZeitEintrag from "@/features/fahrer/components/ZeitEintrag.vue";
import ZeitBox from "@/features/fahrer/components/ZeitBox.vue";
import BelegEinreichen from "@/features/fahrer/actions/BelegEinreichen.vue";
import BelegEintrag from "@/features/fahrer/components/BelegEintrag.vue";
import FahrerAbrechnungsstatus from "./FahrerAbrechnungsstatus.vue";
import { useNextStartzeit } from "@/features/fahrer/composables/useNextStartzeit";
import FahrerAbrechnenRueckgaengig from "../actions/FahrerAbrechnenRueckgaengig.vue";
import FahrerAbrechnen from "../actions/FahrerAbrechnen.vue";

const props = defineProps<{
  auftragId: string;
  fahrerId: string;
  auftragAbholung: string | null;
  query: () => void;
}>();
const { auftragId, fahrerId, auftragAbholung, query: topQuery } = toRefs(props);

const {
  fahrer,
  zeiten,
  belege,
  query: fahrerQuery,
  blatt,
  abrechnungsColor,
} = useAuftragFahrer(auftragId.value, fahrerId.value);

const { nextStartzeit } = useNextStartzeit(zeiten, undefined, auftragAbholung);

const allQuery = () => {
  fahrerQuery();
  topQuery.value();
};
</script>

<template>
  <div class="">
    <div v-if="!fahrer">
      <CircleLoader class="text-white" />
    </div>
    <div v-else>
      <div class="max-w-6xl">
        <WhiteBox class="relative w-full max-w-none">
          <template #inset>
            <div
              class="absolute border-4 inset-0.5 z-0"
              :class="abrechnungsColor"
            ></div>
          </template>
          <template #heading>
            <h2 class="text-lg font-bold">
              {{ fahrer.anzeige_name }}
              <FahrerAbrechnungsstatus
                :abrechnungsstatus="fahrer.abrechnungsstatus"
              />
            </h2>
          </template>
          <template #actions>
            <FahrerAbrechnen
              :fahrer-id="fahrer.id"
              :auftrag-id="auftragId"
              :query="allQuery"
              text="Abrechnen"
              :abrechnungsstatus="fahrer.abrechnungsstatus"
            />
            <FahrerAbrechnenRueckgaengig
              :fahrer-id="fahrer.id"
              :auftrag-id="auftragId"
              :query="allQuery"
              text="Rückgängig"
              :abrechnungsstatus="fahrer.abrechnungsstatus"
            />
          </template>
          <div class="relative z-10 grid w-full grid-cols-2 gap-8">
            <ul class="space-y-3">
              <ZeitBox :datum="auftragAbholung">Geplante Abholung</ZeitBox>
              <template v-for="zeit in zeiten" :key="zeit.id">
                <ZeitEintrag
                  v-if="blatt"
                  :zeit="zeit"
                  :query="allQuery"
                  :auftrag-id="auftragId"
                  :blatt-id="blatt.id"
                />
              </template>
              <li class="flex justify-end">
                <FahrzeitEintragen
                  :query="allQuery"
                  :auftrag-id="auftragId"
                  :fahrer-id="fahrerId"
                  :startzeit="nextStartzeit"
                />
              </li>
            </ul>
            <ul class="space-y-3">
              <template v-for="beleg in belege" :key="beleg.id">
                <BelegEintrag
                  :beleg="beleg"
                  :query="allQuery"
                  :auftrag-id="auftragId"
                />
              </template>
              <li class="flex justify-end">
                <BelegEinreichen
                  :query="allQuery"
                  :auftrag-id="auftragId"
                  :fahrer-id="fahrerId"
                />
              </li>
            </ul>
          </div>
        </WhiteBox>
      </div>
    </div>
  </div>
</template>
